/**
 * 本地水印插件 - 仅供公司内部使用
 * 使用方法：
 * 1. 将整个 local-watermark-plugin 文件夹复制到项目的 src/plugins/ 目录下
 * 2. 在 main.js 中引入：import WatermarkPlugin from '@/plugins/local-watermark-plugin'
 * 3. 注册插件：Vue.use(WatermarkPlugin, options)
 */

import WatermarkManager from './WatermarkManager'
import { applyElementWatermark } from './utils'

// 默认配置
const DEFAULT_OPTIONS = {
  content: '', // 水印内容，支持字符串或字符串数组
  fontSize: 14, // 字体大小
  fontFamily: 'sans-serif', // 字体族
  color: 'rgba(0,0,0,0.15)', // 字体颜色
  opacity: 0.15, // 透明度
  rotate: -20, // 旋转角度（度）
  gapX: 200, // 水平间距
  gapY: 150, // 垂直间距
  offsetX: 50, // 水平偏移
  offsetY: 50, // 垂直偏移
  lineHeight: 22, // 行高
  zIndex: 2147483647, // 层级
  containerId: 'company-watermark', // 容器ID
  autoResize: true, // 自动调整大小
  antiTamper: true // 防篡改
}

/**
 * Vue插件安装函数
 * @param {Object} Vue Vue构造函数
 * @param {Object} options 插件选项
 */
function install(Vue, options = {}) {
  // 合并默认配置
  const config = { ...DEFAULT_OPTIONS, ...options }

  // 创建全局水印管理器实例
  const globalManager = new WatermarkManager(config)

  // 创建API对象
  const watermarkAPI = {
    /**
     * 应用全局水印
     * @param {Object} opts 水印选项
     */
    apply(opts = {}) {
      if (opts && Object.keys(opts).length) {
        globalManager.setOptions(opts)
      }
      globalManager.apply()
      return this
    },

    /**
     * 清除全局水印
     */
    clear() {
      globalManager.clear()
      return this
    },

    /**
     * 切换水印显示状态
     * @param {Boolean} flag 是否显示
     */
    toggle(flag) {
      globalManager.toggle(flag)
      return this
    },

    /**
     * 设置水印选项
     * @param {Object} opts 水印选项
     */
    setOptions(opts = {}) {
      globalManager.setOptions(opts)
      return this
    },

    /**
     * 获取当前水印状态
     */
    get enabled() {
      return globalManager.enabled
    },

    /**
     * 获取当前配置
     */
    get options() {
      return globalManager.options
    }
  }

  // 挂载到Vue原型上
  Vue.prototype.$watermark = watermarkAPI

  // 注册全局指令
  Vue.directive('watermark', {
    /**
     * 指令绑定到元素时调用
     */
    inserted(el, binding) {
      const opts = binding.value || {}
      const destroy = applyElementWatermark(el, opts)
      el.__watermarkDestroy__ = destroy
    },

    /**
     * 指令值更新时调用
     */
    update(el, binding) {
      if (binding.oldValue !== binding.value) {
        if (el.__watermarkDestroy__) {
          el.__watermarkDestroy__()
        }
        const opts = binding.value || {}
        const destroy = applyElementWatermark(el, opts)
        el.__watermarkDestroy__ = destroy
      }
    },

    /**
     * 指令从元素解绑时调用
     */
    unbind(el) {
      if (el.__watermarkDestroy__) {
        el.__watermarkDestroy__()
        delete el.__watermarkDestroy__
      }
    }
  })

  // 提供全局方法
  Vue.watermark = watermarkAPI
}

// 导出插件
export default {
  install,
  WatermarkManager,
  applyElementWatermark,
  DEFAULT_OPTIONS
}

// 命名导出
export {
  WatermarkManager,
  applyElementWatermark,
  DEFAULT_OPTIONS
}
